import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Divider, Typography, Chip, Link, Button } from "@castlery/fortress";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useChatStore } from "@/stores/chatStore";
import { formatCurrency } from "@/utils";
import { UtmUrlHelper } from "@/utils/utm";
import {
  BUTTON_TAG,
  CLICK_ACTION,
  TRACK_EVENT,
  LINK_TAG,
  trackEvent,
} from "@/utils/analytics";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorContent } from "./Message";
import { PreviewImage } from "@/components/ui/image";

interface BundleLineItem {
  quantity: number;
  variant: {
    id: string;
    product_name: string;
    list_price: string;
    price: string;
    images: {
      links: {
        small: string;
      };
    }[];
  };
}
interface CartItem {
  product_type: string;
  onepiece_product_page_url: string;
  quantity: number;
  manual_discount_total: string;
  amount: string;
  is_gift: boolean;
  variant: {
    id: string;
    product_name: string;
    list_price: string;
    price: string;
    images: {
      links: {
        small: string;
      };
    }[];
  };
  bundle_line_items?: BundleLineItem[];
}

/**
 * 计算商品划线价
 * @param {*} item
 * @returns
 */
export const calcItemStrikeThroughPrice = (item: CartItem) => {
  if (!item || Object.keys(item).length === 0) return null;
  if (item.product_type === "bundle") {
    return item.bundle_line_items?.reduce((acr, cur) => {
      return (
        acr +
        ((+Number(cur.variant?.list_price) || 0) * (+cur.quantity || 1) || 0) *
          (item.quantity || 1)
      );
    }, 0);
  }
  return (+item.variant?.list_price || 0) * (+item.quantity || 1) || null;
};

// 单个购物车商品组件
const CartItem: React.FC<{ item: CartItem }> = ({ item }) => {
  const {
    is_gift,
    amount,
    manual_discount_total,
    quantity,
    bundle_line_items,
    variant,
    onepiece_product_page_url,
  } = item;

  const utmLink = UtmUrlHelper.generateUtmUrl(
    onepiece_product_page_url,
    variant?.product_name,
  );

  return (
    <div className="flex flex-col space-y-1 text-base">
      <div className="flex items-center space-x-4 cursor-pointer">
        {/* 商品图片 */}
        <div className="flex-shrink-0 w-[100px] h-[100px]">
          <PreviewImage
            src={variant?.images[0]?.links.small}
            alt={variant?.product_name}
          />
        </div>

        {/* 商品信息 */}
        <div className="flex-1 min-w-0 ">
          {/* 商品名称和赠品标识 */}
          <div
            className="flex justify-between items-start mb-1"
            onClick={() => {
              trackEvent({
                event: TRACK_EVENT,
                category: "cart",
                action: CLICK_ACTION,
                tag: LINK_TAG,
                tag_value: "product_link",
              });
              window.open(utmLink, "_blank");
            }}
          >
            <Typography level="body2">{variant?.product_name}</Typography>
            {is_gift && <Chip>Gift</Chip>}
          </div>

          {/* 商品数量和价格 */}
          <div className="flex justify-between items-center">
            <Typography level="body2">x {quantity}</Typography>
            <div className="flex items-center gap-2">
              {is_gift ? (
                <Typography level="body2" color="neutral">
                  FREE
                </Typography>
              ) : (
                <>
                  <Typography level="body2">
                    {formatCurrency(Number(item.amount), "SGD")}
                  </Typography>
                  {variant?.list_price > variant?.price &&
                    amount + manual_discount_total && (
                      <Typography
                        level="body2"
                        className="text-[#9E9E9E] line-through"
                      >
                        {formatCurrency(
                          calcItemStrikeThroughPrice(item) || 0,
                          "SGD",
                        )}
                      </Typography>
                    )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {bundle_line_items && bundle_line_items.length > 0 && (
        <div className="flex flex-col space-y-1 pl-6 text-sm">
          {bundle_line_items?.map((item: BundleLineItem) => (
            <div key={item.variant.id} className="flex items-center gap-3">
              <div className="w-[75px] h-[50px] rounded-md flex-shrink-0">
                <PreviewImage
                  src={item.variant?.images[0].links?.small}
                  alt={item.variant?.product_name}
                  className="w-[75px] h-[50px] rounded-md flex-shrink-0"
                />
              </div>
              <Typography level="caption1">
                {item.quantity + " x " + item?.variant?.product_name}
              </Typography>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const CartCardWithoutErrorBoundary = (props: { data: any }) => {
  const { line_items = [] } = props.data;

  const { market } = useChatStore();
  const [isExpanded, setIsExpanded] = useState(false);

  const maxVisibleItems = 3;
  const hasMoreItems = line_items.length > maxVisibleItems;
  const visibleItems = isExpanded
    ? line_items
    : line_items.slice(0, maxVisibleItems);

  if (line_items.length === 0) {
    return null;
  }

  const handleCheckout = () => {
    trackEvent({
      event: TRACK_EVENT,
      category: "cart",
      action: CLICK_ACTION,
      tag: BUTTON_TAG,
      tag_value: "checkout",
    });

    if (market) {
      const currentHost = window.location.origin;
      const path = `${currentHost}/${market.toLowerCase()}/checkout/account`;
      window.open(path, "_blank");
    }
  };

  return (
    <Card className="w-full rounded-tl-none border-[#F6F3E7] bg-[#FBF9F4] text-[#3C101E]">
      <CardContent className="p-3 flex flex-col font-aime">
        {/* 商品列表 */}
        <div>
          {visibleItems.map((item: CartItem, index: number) => (
            <div key={`cart-${item.variant.product_name}-${index}`}>
              <CartItem item={item} />
              {/* 分割线 - 除了最后一个商品 */}
              {index < visibleItems.length - 1 && <Divider className="!my-4" />}
            </div>
          ))}
        </div>

        {/* View All Items / Fold Items 按钮 */}
        {hasMoreItems && (
          <div className="flex items-center justify-center py-3">
            <Link
              className="!pb-4"
              variant="secondary"
              size="xs"
              endDecorator={
                isExpanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )
              }
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? "Fold Items" : "View All Items"}
            </Link>
          </div>
        )}

        {/* CHECKOUT 按钮 */}
        <Button className="!mt-3 w-full" onClick={handleCheckout}>
          CHECKOUT
        </Button>
      </CardContent>
    </Card>
  );
};

export const CartCard = (props: { data: any }) => {
  return (
    <ErrorBoundary fallback={<ErrorContent position="left" />}>
      <CartCardWithoutErrorBoundary {...props} />
    </ErrorBoundary>
  );
};
